import { Logger } from "$lib/core/js/util/logger";

const logger = new Logger("actions");

let isSetup = false;

/**
 * Sets up any global/static state for the Actions API.
 * @returns {void}
 */
function staticSetup() {
	if (isSetup) return;
	isSetup = true;
	if (window) {
		Logger.addLogger("actions");
	}
}

/**
 * 
 * @param {HTMLElement} element 
 * @returns {boolean}
 */
export function hasActions(element) {
	return Array.isArray(/** @type {any} */ (element)._$$actions);
}

/**
 * Returns action information for the element.
 * @param {HTMLElement} element 
 * @returns {import("../public").ActionData}
 */
function getActions(element) {
	const e = /** @type {any} */ (element);
	if (typeof(e._$$actions) === "undefined") {
		e._$$actions = {
			actions: [],
			mounted: false,
		};
	}
	return e._$$actions;
}

/**
 * @param {HTMLElement} element 
 */
export function mount(element) {
	const ev = new CustomEvent("mount", { detail: { element } });
	element.dispatchEvent(ev);
}

/**
 * @param {HTMLElement} element 
 */
export function update(element) {
	const ev = new CustomEvent("update", { detail: { element } });
	element.dispatchEvent(ev);
}

/**
 * @param {HTMLElement} element 
 */
export function unmount(element) {
	const ev = new CustomEvent("unmount", { detail: { element } });
	element.dispatchEvent(ev);
}

/**
 * @param {Event} ev
 */
async function onMountHandler(ev) {
	if (ev instanceof CustomEvent) {
		/** @type {HTMLElement} */
		const element = ev.detail.element;

		const data = getActions(element);
		for (let info of data.actions) {
			const act = /** @type {import("../public").ActionInfo} */ (info);
			await Promise.resolve(act.register?.());
		}
		data.mounted = true;
	}
}

/**
 * @param {Event} ev
 */
async function onUpdateHandler(ev) {
	if (ev instanceof CustomEvent) {
		/** @type {HTMLElement} */
		const element = ev.detail.element;

		const data = getActions(element);
		const { actions } = data;
		for (let info of actions) {
			const act = /** @type {import("../public").ActionInfo} */ (info);
			await Promise.resolve(act.update?.(element, act.state));
		}
	}
}

/**
 * @param {Event} ev
 */
async function onUnmountHandler(ev) {
	if (ev instanceof CustomEvent) {
		/** @type {HTMLElement} */
		const element = ev.detail.element;

		const data = getActions(element);
		const { actions } = data;
		const reversed = actions.toReversed();
		for (let info of reversed) {
			const act = /** @type {import("../public").ActionInfo} */ (info);
			await Promise.resolve(act.destroy?.(element, act.state));
		}
		data.mounted = false;
	}
}

/**
 * @template {HTMLElement} TElement
 * @template {Record<string, any>} TState
 * @param {TElement} element The element to which an action should be attached.
 * @param {import("../public").ActionCallback<TElement, TState>} callback The logic that should execute when the element is mounted. Return a function for a destroy callback or an object with `update` and `destroy` functions.
 * @param {TState | {}} [state={}] The state to use across callbacks for this action.
 */
export function action(element, callback, state = {}) {
	staticSetup();

	const data = getActions(element);
	const { actions } = data;

	const s = /** @type {TState} */ (state ?? {});

	/** @type {import("../public").ActionInfo<TElement, TState>} */
	const info = {
		mount: callback,
		state: s,
		update: null,
		destroy: null,
		register: null,
		unregister: null,
		mounted: false,
	}

	/**
	 * Preps the entity to respond to mount/update/unmount events which in turn trigger the actual callbacks.
	 * @returns {Promise<void>}
	 */
	info.register = async () => {
		try {
			const result = await Promise.resolve(info.mount?.(element, s));
			if (!result) {
				// callback returns void or Promise<void>.
				return;
			}

			if (typeof(result) === "function") {
				info.destroy = result;
			} else if (typeof(result) === "object") {
				info.update = result.update ?? null;
				info.destroy = result.destroy ?? null;
			}

			element.addEventListener("update", onUpdateHandler);
			element.addEventListener("unmount", onUnmountHandler);

			info.mounted = true;
		}
		catch (err) {
			logger.error("Failed to resolve async action callback: ", err, info.mount);
		}
	}

	info.unregister = () => {
		if (!info.mounted) return;
		const idx = actions.findIndex(v => v === info);
		if (idx === -1) return;
		actions.splice(idx, 1);
		if (actions.length <= 0) {
			element.removeEventListener("mount", onMountHandler);
			element.removeEventListener("update", onUpdateHandler);
			element.removeEventListener("unmount", onUnmountHandler);
		}
	}

	actions.push(info);

	element.addEventListener("mount", onMountHandler);

	return info.unregister;
}